import React, { useState, useEffect } from "react";

import ShipeeziGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { Location, DifferentLocation } from "../../../models/location";
import { Searches } from "../../../models/shipeezi.table";
import { LocationTypes, Status } from "../../../util/enums";
import BadgeStatus from "../BadgeStatus";
import LocationsModal from "./LocationsModal";

interface Props {
  viewOnly?: boolean;
  loading?: boolean;
  locations?: Location[] | DifferentLocation[];
  onChange?: (locations: Location[] | DifferentLocation[]) => void;
  calledFrom?: string;
}

const Locations = (props: Props) => {
  const { loading, viewOnly: gridInViewMode, locations, onChange, calledFrom } = props;

  const [searches, setSearches] = useState<Searches>([]);

  const [locationSelected, setLocationSelected] = useState<DifferentLocation>();
  const [indexSelected, setIndexSelected] = useState<number>();

  const [_locations, setLocations] = useState<DifferentLocation[]>([]);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState<boolean>(false);
  const [isDuplicateMainLocation, setIsDuplicatieMainLocation] = useState<boolean>(false);

  const [viewOnly, setViewOnly] = useState<boolean>(false);

  const { t: translate } = useTranslation();

  useEffect(() => {
    setLocations(filterLocations(locations || []));
  }, [locations, searches]);

  const filterLocations = (locations: DifferentLocation[]) => {
    let locationsFiltered: DifferentLocation[] = locations || [];

    if (searches && searches?.length > 0) {
      let status: string = "";
      let name: string = "";

      searches.forEach((search) => {
        Object.keys(search).forEach((key) => {
          if (key === "name" && search?.name) name = search?.name;
          if (key === "status" && search?.status) status = search?.status;
        });
      });

      if (locations && locations.length > 0) {
        locationsFiltered = locations.filter((location) => {
          const locationName = location?.name?.toLowerCase() || "";
          const locationStatus = location?.status?.toLowerCase() || "";

          return (
            locationName.includes(name.toLowerCase()) &&
            locationStatus.includes(status.toLowerCase())
          );
        });
      }
    }

    return locationsFiltered;
  };

  const columns: Column[] = [
    { name: "name", title: translate("name") },
    {
      name: "longAddress",
      title: translate("address"),
      formatText: (longAddress: string) => longAddress || "",
      hideSearch: true,
      hideSort: true,
    },
    {
      name: "status",
      title: translate("status"),
      formatComponent: (status: string) => <BadgeStatus value={status} />,
    },
  ];

  const handleChange = (locationUpdated: DifferentLocation) => {
    const locationsUpdated = [...(locations || [])];

    if (locationsUpdated && locationsUpdated?.length > 0) {
      const indexFound = locationsUpdated.findIndex(
        (location: DifferentLocation) =>
          (location?.uuid && location?.uuid === locationUpdated?.uuid) ||
          (location?.id && location?.id === locationUpdated?.id)
      );

      if (indexFound !== -1) {
        locationsUpdated[Number(indexFound)] = {
          ...locationsUpdated[Number(indexFound)],
          ...locationUpdated,
        };
        if (isDuplicateMainLocation && locationUpdated.type === LocationTypes.MAIN) {
          locationsUpdated.push({
            name: `${locationUpdated?.name || ""} ${translate("delivery").toLowerCase()}`,
            status: locationUpdated?.status || Status.ACTIVE,
            longAddress: locationUpdated?.longAddress || "",
            suburb: locationUpdated?.suburb || "",
            state: locationUpdated?.state || "",
            postalcode: locationUpdated?.postalcode || "",
            country: locationUpdated?.country || "",
            companyId: locationUpdated?.companyId,
            type: LocationTypes.DELIVERY,
            dcCode: locationUpdated?.dcCode || "",
            uuid: uuid(),
          });
        }

        setIsDuplicatieMainLocation(false);

        if (onChange) onChange(locationsUpdated);
      }
    }
  };

  const handleClose = (location?: DifferentLocation) => {
    setIsLocationModalOpen(false);
    setViewOnly(false);
    if (location) {
      if (indexSelected !== undefined) {
        handleChange(location);
      } else {
        const locationsUpdated = [location, ...(locations || [])];

        if (onChange) onChange(locationsUpdated);
      }
    }
  };

  const editLocation = (rowId: number | string, location: DifferentLocation) => {
    setIndexSelected(Number(rowId));
    setLocationSelected(location);
    setIsLocationModalOpen(true);
  };

  const viewLocation = (location: DifferentLocation) => {
    setLocationSelected(location);
    setIsLocationModalOpen(true);
    setViewOnly(true);
  };

  const clearLocationAndAddNew = () => {
    setLocationSelected(undefined);
    setIndexSelected(undefined);
    setIsLocationModalOpen(true);
  };

  return (
    <>
      <LocationsModal
        open={isLocationModalOpen}
        handleClose={handleClose}
        onDuplicateLocation={setIsDuplicatieMainLocation}
        location={locationSelected}
        view={viewOnly}
        calledFrom={calledFrom}
      />
      <ShipeeziGrid
        loadingData={loading}
        namePage={translate("locations")}
        data={_locations}
        columns={columns}
        rowActions={{
          handleEdit: !gridInViewMode ? editLocation : undefined,
          handleDetails: (rowId, location: DifferentLocation) => viewLocation(location),
          handleChange: !gridInViewMode
            ? (rowId, location: DifferentLocation) =>
                handleChange({
                  ...location,
                  status: location?.status === "Active" ? "Inactive" : "Active",
                })
            : undefined,
        }}
        keyExtractor={(row: DifferentLocation, index) =>
          index?.toString() || row?.longAddress || ""
        }
        emptyData={translate("noInformationToBeShown")}
        showSelection={true}
        generalActions={{
          orderBy: {
            left: ["handleSearch"],
            right: !gridInViewMode ? ["handleFilterBy", "handleNewRecord"] : ["handleFilterBy"],
          },
          handleNewRecord: {
            text: translate("create"),
            action: () => clearLocationAndAddNew(),
            tooltip: "Add new location",
          },
          handleSearch: {
            onSearch: (filters) => setSearches(filters),
          },
        }}
        positionActionsColumn={"right"}
        showHeaderGrid={true}
        showFooterGrid={true}
      />
    </>
  );
};

export default Locations;
